import styled from '@emotion/styled';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Capability, Capacity, PricingPlan, Support } from './pricingPlans';

export const ComparisonDescriptions: Record<Capacity, string> = {
  [Capacity.CLOUD_REGIONS]: 'Cloud Regions',
  [Capacity.CLOUD_STORAGE_GB]: 'Cloud Storage',
  [Capacity.DEVELOPERS]: 'Developer Users',
  [Capacity.ENVIRONMENTS]: 'Environments',
  [Capacity.NUMBER_OF_APPS]: 'Apps'
};

export const CapabilityDescriptions: Record<Capability, string> = {
  [Capability.FULLSTACK_DEVELOPMENT_PLATFORM]: 'Full-Stack App Development Platform',
  [Capability.CLOUDCODE_SERVERLESS_INTEGRATIONS]: 'CloudCode Engine',
  [Capability.POWERSYNC_INTEGRATION]:
    'PowerSync for Full Relational Offline Data Sync and Intelligent Data Partitioning',
  [Capability.STANDARD_SECURITY_FEATURES]: 'Standard Security Features',
  [Capability.CUSTOM_BRANDING]: 'Custom-Branded Apps',
  [Capability.SSO_INTEGRATION]: 'SSO Integration',
  [Capability.GITHUB_VERSION_CONTROL]: 'GitHub Version Control Integration',
  [Capability.CLOUDCODE_WEB_TASKS]: 'CloudCode Web Tasks',
  [Capability.NFC_ENABLED_APPS]: 'NFC-Enabled Apps',
  [Capability.SQL_DATA_PIPELINES]: 'SQL Data Pipeline',
  [Capability.ADDITIONAL_SECURITY]: 'Additional Security Features',
  [Capability.ADVANCED_HARDWARE_CAPABILITIES]: 'Advanced Hardware Capabilities',
  [Capability.MODEL_VIEWER_3D]: '3D Model Viewer',
  [Capability.POWERBI_DASHBOARDS]: 'In-App Embedded Power BI Dashboards',
  [Capability.FINE_GRAINED_PERMISSIONS]: 'Fine-Grained Developer Permissions',
  [Capability.APP_PACKAGES]: 'App Packages'
};

export const SupportDescriptions: Record<Support, string> = {
  [Support.SLA]: 'Service Level Agreement',
  [Support.ONBOARDING_SESSION]: 'Onboarding Session',
  [Support.DISCORD_SUPPORT]: 'Community Discord',
  [Support.DOCS]: 'Online Documentation',
  [Support.HELPDESK]: 'Developer Helpdesk',
  [Support.CLOUD_UPTIME_GUARANTEE]: 'Cloud Uptime Guarantee',
  [Support.CUSTOMER_SUCCESS_MANAGER]: 'Customer Success Manager',
  [Support.INCIDENT_RESPONSE]: 'Incident Response Times',
  [Support.INCIDENT_RESOLUTION_TIME]: 'Guaranteed Incident Resolution Times'
};

export interface ComparisonTableProps {
  plans: PricingPlan[];
  className?: any;
}

function formatText(value: string | number | string[] | number[]): string {
  if (Array.isArray(value)) {
    return value.map(formatText).join(', ');
  }

  if (typeof value === 'string') {
    return value[0].toUpperCase() + value.slice(1);
  }

  return `${value}`;
}

export const ComparisonTable: React.FC<ComparisonTableProps> = (props) => {
  const { plans } = props;

  const headings = (sectionName: string) => (
    <>
      <S.SectionHeaderCell>
        <S.SectionHeader>{sectionName}</S.SectionHeader>
      </S.SectionHeaderCell>
      {plans.map((plan, key) => (
        <S.HeadingCell key={key}>
          <S.Name index={key}>{plan.name}</S.Name>
        </S.HeadingCell>
      ))}
    </>
  );

  const capacityRows = Object.values(Capacity).map((comparisonKey) => {
    return (
      <S.Row key={comparisonKey}>
        <S.DescriptionCell>{ComparisonDescriptions[comparisonKey]}</S.DescriptionCell>
        {plans.map((plan, key) => (
          <S.Cell key={key}>{formatText(plan.capacities[comparisonKey])}</S.Cell>
        ))}
      </S.Row>
    );
  });

  const capabilityRows = Object.values(Capability).map((comparisonKey) => {
    return (
      <S.Row key={comparisonKey}>
        <S.DescriptionCell>{CapabilityDescriptions[comparisonKey]}</S.DescriptionCell>
        {plans.map((plan, key) => {
          const val = plan.capabilities[comparisonKey];

          if (val === true) {
            return (
              <S.Cell key={key}>
                <S.Icon icon={faCheckCircle} index={key} />
              </S.Cell>
            );
          }

          if (val === false) {
            return <S.Cell key={key}>-</S.Cell>;
          }

          return <S.Cell key={key}>{val}</S.Cell>;
        })}
      </S.Row>
    );
  });

  const supportRows = Object.values(Support).map((comparisonKey) => {
    return (
      <S.Row key={comparisonKey}>
        <S.DescriptionCell>{SupportDescriptions[comparisonKey]}</S.DescriptionCell>
        {plans.map((plan, key) => {
          const val = plan.support[comparisonKey];

          if (val === true) {
            return (
              <S.Cell key={key}>
                <S.Icon icon={faCheckCircle} index={key} />
              </S.Cell>
            );
          }

          if (val === false) {
            return <S.Cell key={key}>-</S.Cell>;
          }

          return <S.Cell key={key}>{val}</S.Cell>;
        })}
      </S.Row>
    );
  });

  return (
    <S.Table className={props.className}>
      <tbody>
        <S.Row>{headings('Capacities')}</S.Row>
        {capacityRows}
        <S.SpacerRow />
        <S.Row>{headings('Capabilities')}</S.Row>
        {capabilityRows}
        <S.SpacerRow />
        <S.Row>{headings('Support')}</S.Row>
        {supportRows}
      </tbody>
    </S.Table>
  );
};

namespace S {
  export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
  `;

  export const Row = styled.tr`
    :last-of-type {
      td {
        border-bottom: 1px solid transparent;
      }
    }
  `;

  export const Cell = styled.td`
    text-align: center;
    font-size: 14px;
    border-bottom: 1px solid ${(p) => p.theme.text.listBorder};
    padding: 11px 10px;
    width: 160px;

    :nth-of-type(even) {
      background: ${(p) => p.theme.card.background};
    }
  `;

  export const DescriptionCell = styled(Cell)`
    text-align: left;
    width: auto;
  `;

  export const HeadingCell = styled.th`
    text-align: center;
  `;

  export const Name = styled.div<{ index: number }>`
    padding: 11px 15px;
    background: ${(p) => p.theme.pricing[`background${p.index}`]};
    color: white;
  `;

  export const SectionHeaderCell = styled.th`
    text-align: left;
  `;

  export const SectionHeader = styled.div`
    padding: 11px 15px;
    background: ${(p) => p.theme.card.background};
  `;

  export const Icon = styled(FontAwesomeIcon)<{ index: number }>`
    color: ${(p) => p.theme.pricing[`checkIcon${p.index}`]};
    padding: 2px;
    font-size: 16px;
  `;

  export const SpacerRow = styled.tr`
    height: 25px;
  `;
}
